/**
 * Floating AI Chat Widget
 *
 * A compact, always-accessible AI support chat that hovers over the app.
 * Opened via a floating action button (FAB) in the bottom-left corner.
 * Designed to be mounted once at the app shell level (HomePage).
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  Slide,
  Badge,
  Tooltip,
  CircularProgress,
  Chip,
  Stack,
  Link,
  useMediaQuery,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SchoolIcon from "@mui/icons-material/School";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import firebase from "firebase/compat/app";
import { useAISupportMessages } from "../../../hooks/useAISupportMessages";
import { useNavigate } from "react-router-dom";
import { marked } from "marked";

interface FloatingAIChatProps {
  /** Optional initial context for the AI (e.g., current page context) */
  context?: string;
}

const QUICK_PROMPTS = [
  "How do I create a training plan?",
  "How do weekly reports work?",
  "How do I add a new client?",
  "What is compliance scoring?",
];

const FloatingAIChat: React.FC<FloatingAIChatProps> = ({ context }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const userId = firebase.auth().currentUser?.uid || "";
  const navigate = useNavigate();
  const isShortViewport = useMediaQuery("(max-height:780px)");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousMessagesLengthRef = useRef(0);

  const { messages, loading, sendMessage, error } = useAISupportMessages({
    userId,
  });

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  useEffect(() => {
    if (!open) return;
    // Avoid animated "top-to-bottom" travel on open; start at latest message immediately.
    scrollToBottom("auto");
  }, [open, scrollToBottom]);

  useEffect(() => {
    if (!open) {
      previousMessagesLengthRef.current = messages.length;
      return;
    }

    if (messages.length > previousMessagesLengthRef.current) {
      scrollToBottom("smooth");
    }

    previousMessagesLengthRef.current = messages.length;
  }, [messages.length, open, scrollToBottom]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    try {
      await sendMessage(text);
    } catch {
      // error handled by hook
    }
  }, [input, loading, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleQuickPrompt = useCallback(
    (prompt: string) => {
      setInput("");
      sendMessage(prompt).catch(() => {});
    },
    [sendMessage],
  );

  const renderMarkdown = useCallback((content: string) => {
    const html = marked.parse(content, { breaks: true, async: false }) as string;
    return { __html: html };
  }, []);

  useEffect(() => {
    if (isShortViewport && open) {
      setOpen(false);
    }
  }, [isShortViewport, open]);

  return (
    <>
      {/* Floating Action Button */}
      {!open && !isShortViewport && (
        <Tooltip title="Ask M45 AI Assistant" placement="right" arrow>
          <Fab
            color="primary"
            onClick={() => setOpen(true)}
            sx={{
              position: "fixed",
              bottom: 60,
              left: 30,
              right: "auto",
              zIndex: 1200,
              width: 56,
              height: 56,
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #818cf8 0%, #6366f1 100%)",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s",
            }}
          >
            <Badge
              variant="dot"
              color="success"
              overlap="circular"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <SmartToyIcon sx={{ fontSize: 28 }} />
            </Badge>
          </Fab>
        </Tooltip>
      )}

      {/* Chat Panel */}
      <Slide direction="up" in={open && !isShortViewport} mountOnEnter unmountOnExit>
        <Paper
          elevation={16}
          sx={{
            position: "fixed",
            bottom: 90,
            left: 24,
            right: "auto",
            width: { xs: "calc(100vw - 48px)", sm: 400 },
            height: { xs: "calc(100vh - 100px)", sm: 540 },
            maxHeight: "80vh",
            zIndex: 1300,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
              color: "white",
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  M45 AI Assistant
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.85, fontSize: "0.65rem" }}
                >
                  {loading ? "Thinking..." : "Ask anything about Mighty45"}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Help Center">
                <IconButton
                  size="small"
                  onClick={() => {
                    navigate("/home/learn");
                    setOpen(false);
                  }}
                  sx={{ color: "white", opacity: 0.8, "&:hover": { opacity: 1 } }}
                >
                  <SchoolIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                sx={{ color: "white", opacity: 0.8, "&:hover": { opacity: 1 } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              px: 2,
              py: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              bgcolor: "#fafbfc",
            }}
          >
            {messages.length === 0 && !loading && (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <SmartToyIcon
                  sx={{ fontSize: 40, color: "#6366f1", opacity: 0.5, mb: 1 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Hi! I'm your Mighty45 assistant. Ask me anything about
                  coaching features, workflows, or how to use the platform.
                </Typography>
                <Stack spacing={0.75}>
                  {QUICK_PROMPTS.map((prompt) => (
                    <Chip
                      key={prompt}
                      label={prompt}
                      variant="outlined"
                      size="small"
                      onClick={() => handleQuickPrompt(prompt)}
                      sx={{
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        height: "auto",
                        py: 0.5,
                        "& .MuiChip-label": { whiteSpace: "normal" },
                        "&:hover": {
                          bgcolor: "#6366f110",
                          borderColor: "#6366f1",
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {messages.map((msg) => {
              const learningSources = (msg.sources || [])
                .filter(
                  (s): s is { id: string; title?: string; slug?: string | null; category?: string | null; app_route?: string | null } =>
                    typeof s === "object" && !!s.slug && s.category === "learning",
                );

              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems:
                      msg.role === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "85%",
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: msg.role === "user" ? "#6366f1" : "white",
                      color: msg.role === "user" ? "white" : "text.primary",
                      boxShadow:
                        msg.role === "user"
                          ? "none"
                          : "0 1px 3px rgba(0,0,0,0.08)",
                      border:
                        msg.role === "user" ? "none" : "1px solid #e5e7eb",
                      overflow: "hidden",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      "& p": {
                        m: 0,
                        fontSize: "0.8rem",
                        lineHeight: 1.5,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        "&:not(:last-child)": { mb: 0.75 },
                      },
                      "& ul, & ol": {
                        m: 0,
                        pl: 2.5,
                        fontSize: "0.8rem",
                        lineHeight: 1.5,
                      },
                      "& li": { mb: 0.25 },
                      "& strong": { fontWeight: 600 },
                      "& code": {
                        bgcolor:
                          msg.role === "user"
                            ? "rgba(255,255,255,0.15)"
                            : "#f3f4f6",
                        px: 0.5,
                        borderRadius: 0.5,
                        fontSize: "0.75rem",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      },
                      "& pre": {
                        maxWidth: "100%",
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        bgcolor: msg.role === "user" ? "rgba(255,255,255,0.1)" : "#f3f4f6",
                        borderRadius: 1,
                        p: 1,
                        my: 0.5,
                      },
                      "& table": {
                        display: "block",
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "auto",
                        borderCollapse: "collapse",
                        fontSize: "0.75rem",
                        lineHeight: 1.35,
                        my: 0.5,
                      },
                      "& th, & td": {
                        border: msg.role === "user" ? "1px solid rgba(255,255,255,0.25)" : "1px solid #d1d5db",
                        px: 0.75,
                        py: 0.5,
                        textAlign: "left",
                        verticalAlign: "top",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      },
                      "& a": {
                        color: msg.role === "user" ? "#c7d2fe" : "#6366f1",
                      },
                      "& h1, & h2, & h3": {
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        mt: 0.5,
                        mb: 0.25,
                      },
                    }}
                  >
                    {msg.role === "user" ? (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.8rem", whiteSpace: "pre-wrap" }}
                      >
                        {msg.content}
                      </Typography>
                    ) : (
                      <Box
                        dangerouslySetInnerHTML={renderMarkdown(msg.content)}
                      />
                    )}
                  </Box>

                  {/* Help Center article source links */}
                  {msg.role === "assistant" && learningSources.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ mt: 0.5, ml: 0.5, flexWrap: "wrap", gap: 0.5 }}
                    >
                      {learningSources.map((src) => (
                        <Chip
                          key={src.slug}
                          icon={<SchoolIcon sx={{ fontSize: "14px !important" }} />}
                          label={src.title || src.slug}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            navigate(`/home/learn/${src.slug}`);
                            setOpen(false);
                          }}
                          sx={{
                            fontSize: "0.65rem",
                            height: 22,
                            cursor: "pointer",
                            borderColor: "#6366f140",
                            "&:hover": {
                              bgcolor: "#6366f110",
                              borderColor: "#6366f1",
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              );
            })}

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: "white",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={14} sx={{ color: "#6366f1" }} />
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Thinking...
                  </Typography>
                </Box>
              </Box>
            )}

            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ textAlign: "center", py: 0.5 }}
              >
                {error}
              </Typography>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Powered-by footer + link to Help Center */}
          <Box
            sx={{
              px: 2,
              py: 0.5,
              borderTop: "1px solid #f0f0f0",
              bgcolor: "#fafbfc",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Link
              component="button"
              variant="caption"
              underline="hover"
              onClick={() => {
                navigate("/home/learn");
                setOpen(false);
              }}
              sx={{ color: "text.disabled", fontSize: "0.65rem" }}
            >
              Browse the Help Center for guides & tutorials
            </Link>
          </Box>

          {/* Input */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderTop: "1px solid #e5e7eb",
              bgcolor: "white",
              display: "flex",
              alignItems: "flex-end",
              gap: 0.5,
            }}
          >
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={3}
              size="small"
              placeholder="Ask a question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: "0.85rem",
                  bgcolor: "#f9fafb",
                  "& fieldset": { borderColor: "#e5e7eb" },
                  "&:hover fieldset": { borderColor: "#6366f1" },
                  "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || loading}
              size="small"
              sx={{
                bgcolor: input.trim() ? "#6366f1" : "transparent",
                color: input.trim() ? "white" : "text.disabled",
                "&:hover": { bgcolor: input.trim() ? "#4f46e5" : "transparent" },
                mb: 0.25,
                width: 36,
                height: 36,
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

export default FloatingAIChat;
